/**
 * Drag and Drop для таблиц спецификации.
 *
 * Sortable.js подключается с CDN, группа 'specification-items' позволяет
 * перетаскивать между таблицами разных комнат. После drop — apiRequest на
 * /api/project/spec/reorder с новым порядком.
 */

import { apiRequest, APIError } from '../../api.js';

let draggedRow = null;
let draggedData = null;
let originalRoom = null;
let tables = [];

function initDragDrop() {
    const specTables = document.querySelectorAll('.adm_spec_item_table');
    if (specTables.length === 0) return;

    specTables.forEach((table, index) => {
        // Обеспечиваем наличие tbody
        let tbody = table.querySelector('tbody');
        if (!tbody) {
            tbody = document.createElement('tbody');
            const rows = Array.from(table.querySelectorAll('tr')).slice(1); // Пропускаем header
            rows.forEach(row => tbody.appendChild(row));
            table.appendChild(tbody);
        }

        const roomElement = tbody.closest('.adm_spec_item');
        const roomCheckbox = roomElement?.querySelector('.checkbox-select-all[data-room]');
        const roomId = roomCheckbox?.dataset.room;
        if (!roomId) {
            console.warn('Spec DnD: no room ID for table', index);
            return;
        }

        const sortable = new Sortable(tbody, {
            group: 'specification-items',
            animation: 150,
            ghostClass: 'sortable-ghost',
            dragClass: 'sortable-drag',
            handle: '.adm_spec_item_table_td_drag',
            forceFallback: false,
            swapThreshold: 0.65,

            onStart(evt) {
                draggedRow = evt.item;
                draggedData = {
                    id: draggedRow.dataset.id,
                    sorting: draggedRow.dataset.sorting,
                    roomId,
                };
                originalRoom = roomId;
                document.body.classList.add('dragging-spec-item');
            },

            onMove() {
                return true;
            },

            onEnd(evt) {
                document.body.classList.remove('dragging-spec-item');

                const newTable = evt.to;
                const newRoomElement = newTable.closest('.adm_spec_item');
                const newRoomCheckbox = newRoomElement?.querySelector('.checkbox-select-all[data-room]');
                const newRoomId = newRoomCheckbox?.dataset.room;
                if (!newRoomId) {
                    console.error('Spec DnD: no new room ID');
                    return;
                }

                const rows = Array.from(newTable.querySelectorAll('tr[data-id]'));
                const sortingData = rows.map((row, i) => ({
                    id: row.dataset.id,
                    sorting: i + 1,
                    room_id: newRoomId,
                }));

                saveOrder(sortingData, draggedData.id, originalRoom, newRoomId);
            },
        });

        tables.push(sortable);
    });

    addDragDropStyles();
}

async function saveOrder(sortingData, itemId, oldRoomId, newRoomId) {
    try {
        const response = await apiRequest('/api/project/spec/reorder', {
            method: 'POST',
            body: {
                items: sortingData,
                moved_item_id: itemId,
                old_room_id: oldRoomId,
                new_room_id: newRoomId,
            },
        });

        if (response && response.success) {
            window.showToast?.('success', 'Order saved successfully');
            // Обновляем data-sorting у строк
            sortingData.forEach(item => {
                const row = document.querySelector(`tr[data-id="${item.id}"]`);
                if (row) row.dataset.sorting = item.sorting;
            });
        } else {
            window.showToast?.('error', response?.message || 'Failed to save order');
        }
    } catch (e) {
        console.error('Spec DnD: save order error:', e);
        const msg = e instanceof APIError ? e.message : 'An error occurred while saving';
        window.showToast?.('error', msg);
    }
}

function addDragDropStyles() {
    if (document.getElementById('spec-drag-drop-styles')) return;

    const style = document.createElement('style');
    style.id = 'spec-drag-drop-styles';
    style.textContent = `
        .adm_spec_item_table_td_drag { cursor: move; cursor: grab; }
        .adm_spec_item_table_td_drag:active { cursor: grabbing; }
        .sortable-ghost { opacity: 0.5; }
        .sortable-ghost td { background: #f1f1f1 !important; vertical-align: middle !important; }
        .sortable-drag { opacity: 0.4 !important; }
        .adm_spec_item_table tbody tr.sortable-ghost td,
        .adm_spec_item_table tbody tr.sortable-drag td {
            vertical-align: middle !important;
            padding: 10px !important;
            padding-left: 0 !important;
            padding-top: 14px !important;
            padding-bottom: 12px !important;
            padding-right: 5px !important;
        }
        .adm_spec_item_table tbody tr.sortable-ghost td:first-child,
        .adm_spec_item_table tbody tr.sortable-drag td:first-child {
            padding-left: 10px !important;
        }
        body.dragging-spec-item { cursor: grabbing !important; }
        .adm_spec_item_table tbody.sortable-chosen { background-color: rgba(240, 248, 255, 0.3); }
    `;
    document.head.appendChild(style);
}

function loadSortableLibrary(callback) {
    if (typeof Sortable !== 'undefined') {
        callback();
        return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/sortablejs@1.15.0/Sortable.min.js';
    script.onload = callback;
    script.onerror = () => console.error('Failed to load Sortable.js');
    document.head.appendChild(script);
}

function init() {
    loadSortableLibrary(() => {
        initDragDrop();

        // Переинициализация после AJAX-обновления таблицы
        document.addEventListener('spec-table-updated', () => {
            tables.forEach(t => t.destroy());
            tables = [];
            setTimeout(initDragDrop, 100);
        });
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}

// Публичный API
window.SpecDragDrop = {
    init: initDragDrop,
    refresh() {
        tables.forEach(t => t.destroy());
        tables = [];
        initDragDrop();
    },
};
